import { useState } from "react"
import type { UseFormReturn } from "react-hook-form"

import { usePaymentTermsContext } from "../contexts/PaymentTermsContext"

import { useCompanyAccountingTermsActions } from "@/screens/CompanySettings/Accounting/hooks/useCompanyAccountingTermsActions"
import { useUpdateVendorPayableFields } from "@/screens/CompanySettings/Accounting/hooks/useUpdateVendorPayableFields"
import type { PaymentTermsFormValues } from "@/screens/CompanySettings/Accounting/schemas/paymentTermsFormSchema"
import { serializeUpsertInput } from "@/screens/CompanySettings/Accounting/utils/common"
import { mapVendorPayableFieldsToUpdateInput } from "@/screens/CompanySettings/Accounting/utils/vendorPayableFields"

interface UsePaymentTermsDialogReturn {
  setIsCancelConfirmationOpen: (isOpen: boolean) => void
  isCancelConfirmationOpen: boolean
  handleSubmit: () => Promise<void>
  canSubmit: boolean
  handleCancel: () => void
}

export const usePaymentTermsDialog = ({
  companyId,
  onSuccess,
  onClose,
  methods,
}: {
  companyId: string
  onSuccess: () => void
  onClose: () => void
  methods: UseFormReturn<PaymentTermsFormValues>
}): UsePaymentTermsDialogReturn => {
  const {
    handleSubmit: zodHandleSubmit,
    formState: { isValid, isDirty, errors, isSubmitting },
  } = methods
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const { isCompanyVendor, isCompanyCustomer } = usePaymentTermsContext()

  const { addAccountingTermData } = useCompanyAccountingTermsActions()
  const { updateVendorPayableFields } = useUpdateVendorPayableFields()

  const handleSubmit = zodHandleSubmit(async (input) => {
    const inputs = serializeUpsertInput(input, isCompanyVendor, isCompanyCustomer)
    const isAccountingTermsSaved = await addAccountingTermData(inputs)

    if (!isAccountingTermsSaved) return

    if (isCompanyVendor) {
      const { sensitiveAccount: isSensitiveAccountDirty, defaultExpenseAccountId: isDefaultExpenseAccountIdDirty } =
        methods.formState.dirtyFields
      if (isSensitiveAccountDirty || isDefaultExpenseAccountIdDirty) {
        const isVendorPayableSaved = await updateVendorPayableFields(
          mapVendorPayableFieldsToUpdateInput(
            { sensitiveAccount: input.sensitiveAccount, defaultExpenseAccountId: input.defaultExpenseAccountId },
            { sensitiveAccount: isSensitiveAccountDirty, defaultExpenseAccountId: isDefaultExpenseAccountIdDirty },
            companyId
          ),
          { silent: true }
        )

        // Keep the dialog open so the user can retry the vendor-payable save;
        // the accounting-terms upsert is idempotent on resubmit.
        if (!isVendorPayableSaved) return
      }
    }

    onSuccess()
  })

  const handleCancel = () => {
    if (isDirty) {
      setIsCancelConfirmationOpen(true)
    } else {
      onClose()
    }
  }

  // Ensure the form is valid, dirty, and has no errors (image upload restrictions) before allowing submission
  const canSubmit = isValid && isDirty && !Object.keys(errors).length && !isSubmitting

  return {
    setIsCancelConfirmationOpen,
    isCancelConfirmationOpen,
    canSubmit,
    handleSubmit,
    handleCancel,
  }
}
